import { useEffect, useRef, type ReactNode } from 'react';
import { Search } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Input } from './Input';

/**
 * The one list-page search box.
 *
 * Five list pages shipped five of these: "Search clients…", "Search…" (of
 * what?), "Search vendor or notes…", "Number or client…" with a `/` key badge,
 * and "Search people…  (press /)" — the shortcut advertised inside the
 * placeholder on one page and as a badge on another, while /clients,
 * /agreements and /expenses implemented it not at all. Same control, five
 * spellings and two different contracts about what the keyboard does.
 *
 * Here it is one thing: `Search <nouns>…`, a leading glyph, and `/` focuses it
 * on every page that renders this, advertised the same way in the same place.
 */
export interface SearchInputProps {
  /** What is being searched, as a noun phrase: "clients", "vendor or notes". */
  searches: string;
  value: string;
  onChange: (value: string) => void;
  /** Screen-reader label; defaults to `Search {searches}`. */
  ariaLabel?: string;
  className?: string;
  /** Set false where `/` is already spoken for (a grid that types into cells). */
  shortcut?: boolean;
  size?: 'sm' | 'md';
  /** Rendered after the field inside the same flex row (rare). */
  trailing?: ReactNode;
}

export function SearchInput({
  searches,
  value,
  onChange,
  ariaLabel,
  className,
  shortcut = true,
  size = 'sm',
}: SearchInputProps) {
  const ref = useRef<HTMLInputElement>(null);

  // "/" focuses the search, the way every list-first tool does it — and never
  // while the caret is already inside a field, or `/` stops being typeable.
  useEffect(() => {
    if (!shortcut) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (
        el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT' ||
          el.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      ref.current?.focus();
      ref.current?.select();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [shortcut]);

  return (
    <div className={cn('relative', className)}>
      <Input
        ref={ref}
        aria-label={ariaLabel ?? `Search ${searches}`}
        size={size}
        icon={<Search />}
        placeholder={`Search ${searches}…`}
        className={shortcut ? 'sm:[&_input]:pr-8' : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') e.currentTarget.blur();
        }}
      />
      {/* A keyboard hint on a touch screen is a lie taking up space in the
          field it describes — desktop only. */}
      {shortcut && (
        <kbd
          aria-hidden
          title="Press / to search"
          className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-grey-200 bg-grey-50 px-1.5 py-0.5 text-caption font-semibold text-grey-400 sm:block"
        >
          /
        </kbd>
      )}
    </div>
  );
}
